import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Injector,
  OnInit,
  inject,
} from '@angular/core';
import { FormControlValueAccessorComponent } from '@vdms-hq/ui';
import { FormArray, FormControl, FormGroup, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FieldsFetcherService } from '@vdms-hq/fields';
import { FormBuilderService } from '@vdms-hq/dynamic-form';
import { map, tap } from 'rxjs/operators';
import { DefinitionForSelectedIdPipe } from './definition-for-selected-id.pipe';
import { ButtonComponent } from '@vdms-hq/ui';
import { ControlComponent } from '@vdms-hq/dynamic-form';
import { FormInputSelectComponent } from '@vdms-hq/ui';
import { AsyncPipe } from '@angular/common';

type OutsideValue = string | undefined;

type CustomValue = string | number | Date | CustomValue[];
type Sibling = {
  key: string;
  value: CustomValue;
};

type InsideValue = {
  siblings: Sibling[];
};

@Component({
  selector: 'vdms-hq-selectors-field-values-edit-extra',
  templateUrl: './field-values-edit-extra.component.html',
  styleUrls: ['./field-values-edit-extra.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FieldValuesEditExtraComponent),
    },
  ],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    FormInputSelectComponent,
    ControlComponent,
    ButtonComponent,
    AsyncPipe,
    DefinitionForSelectedIdPipe,
  ],
})
export class FieldValuesEditExtraComponent
  extends FormControlValueAccessorComponent<OutsideValue, InsideValue>
  implements OnInit
{
  private fields = inject(FieldsFetcherService);
  private formBuilder = inject(FormBuilderService);

  innerFormControl = new FormGroup({
    siblings: new FormArray([FieldValuesEditExtraComponent.createCustomParent()]),
  });

  allFieldsSelectOptions$ = this.fields.getAssetViewConfiguration$('asset_details').pipe(
    tap((fields) => {
      this.formBuilder.set(fields.definitions);
      this.formBuilder.unlock();
    }),
    map((fields) => fields.definitions.filter((field) => !field.input.readonly)),
    map((items) =>
      items.map((item) => ({
        key: item.id,
        label: `${item.label} (${item.id})`,
      })),
    ),
  );

  static createCustomParent(value?: Partial<Sibling>) {
    return new FormGroup({
      key: new FormControl<Sibling['key'] | ''>(value?.key ?? ''),
      value: new FormControl<Sibling['value'] | ''>(value?.value ?? ''),
    });
  }

  constructor() {
    const injector = inject(Injector);
    const changeDetectorRef = inject(ChangeDetectorRef);

    super(injector, changeDetectorRef);
  }

  get siblings() {
    return this.innerFormControl.controls.siblings;
  }

  ngOnInit() {
    super.ngOnInit();
    this.formBuilder.unlock();
  }

  addSibling() {
    this.siblings.push(FieldValuesEditExtraComponent.createCustomParent());
  }

  removeSibling(index: number) {
    this.siblings.removeAt(index);
  }

  transformInnerToOuter(value: InsideValue): OutsideValue {
    const filtered = {
      siblings: value.siblings.filter(({ key, value }) => !!key && !!value),
    };

    return JSON.stringify(filtered);
  }

  transformOuterToInner(value: OutsideValue): InsideValue {
    if (!value) {
      return {
        siblings: [],
      };
    }

    return JSON.parse(value);
  }

  writeValue(value: OutsideValue): void {
    if (!value) return;
    this.removeSibling(0);

    this.outerValue = value;
    const inner = this.transformOuterToInner(value);
    (inner.siblings ?? []).forEach((item) => {
      this.siblings.push(FieldValuesEditExtraComponent.createCustomParent(item));
    });
    this.innerFormControl.patchValue(inner);
  }
}
